// src/cockpit4/components/GeoStrategyCanvas.tsx
// ============================================
// P1-1 策略画布：内容方向 → 矩阵画布（双视图）
//   视图一：内容类型 × 用户意图
//   视图二：目标人群 × 用户意图（按关键词/标题推断人群）
//   颜色 = 该格平均 ROI 强度；点击格子看落位的内容方向
// ============================================

import { useMemo, useState } from "react";
import { Grid3x3, Users, X } from "lucide-react";

type Intent = "informational" | "commercial" | "transactional" | "navigational";
type ContentType = "tutorial" | "analysis" | "case_study" | "opinion" | "news";
type Audience = "exec" | "marketing" | "tech" | "business" | "creator" | "general";
type ViewMode = "type" | "audience";

interface Dir {
  title: string;
  keyword: string;
  contentType: ContentType;
  roiScore: number;
}

interface Kw {
  keyword: string;
  intent: Intent;
}

const INTENT_META: Record<Intent, { label: string; color: string }> = {
  informational: { label: "认知了解", color: "#3B82F6" },
  commercial: { label: "商业对比", color: "#F59E0B" },
  transactional: { label: "成交转化", color: "#10B981" },
  navigational: { label: "品牌直达", color: "#A855F7" },
};

const TYPE_META: Record<ContentType, { label: string; color: string }> = {
  tutorial: { label: "教程", color: "#3B82F6" },
  analysis: { label: "分析", color: "#10B981" },
  case_study: { label: "案例", color: "#F59E0B" },
  opinion: { label: "观点", color: "#EC4899" },
  news: { label: "资讯", color: "#06B6D4" },
};

const AUDIENCE_META: Record<Audience, { label: string; color: string }> = {
  exec: { label: "企业决策层", color: "#A855F7" },
  marketing: { label: "市场/增长", color: "#22D3EE" },
  tech: { label: "技术/产品", color: "#3B82F6" },
  business: { label: "实体商家", color: "#F59E0B" },
  creator: { label: "个人创作者", color: "#EC4899" },
  general: { label: "通用受众", color: "#6B7280" },
};

const INTENTS: Intent[] = ["informational", "commercial", "transactional", "navigational"];
const TYPES: ContentType[] = ["tutorial", "analysis", "case_study", "opinion", "news"];
const AUDIENCES: Audience[] = ["exec", "marketing", "tech", "business", "creator", "general"];

function inferIntent(d: Dir, kwMap: Map<string, Intent>): Intent {
  const k = kwMap.get(d.keyword);
  if (k) return k;
  const text = `${d.title}${d.keyword}`;
  if (/价格|购买|多少钱|报价|套餐|试用|下载|联系|开通/.test(text)) return "transactional";
  if (/哪家|推荐|对比|好不好|性价比|选择|怎么选|评测/.test(text)) return "commercial";
  if (/XEAOS|小舍|官网/.test(text)) return "navigational";
  return "informational";
}

function inferAudience(d: Dir): Audience {
  const text = `${d.title}${d.keyword}`;
  if (/企业|公司|团队|组织|管理|CEO|老板|决策|转型/.test(text)) return "exec";
  if (/营销|获客|增长|投放|转化|品牌|市场|线索|GEO|DSO/.test(text)) return "marketing";
  if (/开发|技术|API|集成|系统|对接|部署|架构|代码/.test(text)) return "tech";
  if (/酒店|餐饮|门店|零售|商家|加盟|店铺|民宿/.test(text)) return "business";
  if (/自媒体|博主|创作者|IP|个人|账号|短视频/.test(text)) return "creator";
  return "general";
}

export default function GeoStrategyCanvas({
  directions,
  keywords,
}: {
  directions: Dir[];
  keywords: Kw[];
}) {
  const [view, setView] = useState<ViewMode>("type");
  const [selected, setSelected] = useState<{ row: string; intent: Intent; type?: ContentType; audience?: Audience } | null>(null);

  const kwMap = useMemo(() => {
    const m = new Map<string, Intent>();
    for (const k of keywords) if (k.keyword) m.set(k.keyword, k.intent);
    return m;
  }, [keywords]);

  const matrix = useMemo(() => {
    const grid: Record<string, { items: Dir[]; sumRoi: number }> = {};
    for (const d of directions) {
      const intent = inferIntent(d, kwMap);
      const audience = inferAudience(d);
      const key = `${intent}|${view === "type" ? d.contentType : audience}`;
      if (!grid[key]) grid[key] = { items: [], sumRoi: 0 };
      grid[key].items.push(d);
      grid[key].sumRoi += d.roiScore || 0;
    }
    return grid;
  }, [directions, kwMap, view]);

  const cellFor = (intent: Intent, row: string) => {
    const c = matrix[`${intent}|${row}`];
    if (!c) return null;
    return { count: c.items.length, avgRoi: Math.round(c.sumRoi / c.items.length), items: c.items };
  };

  const roiColor = (avg: number) => {
    if (avg >= 80) return "#10B981";
    if (avg >= 60) return "#F59E0B";
    return "#EF4444";
  };

  const rows = view === "type"
    ? TYPES.map((tp) => ({ key: tp, label: TYPE_META[tp].label, color: TYPE_META[tp].color }))
    : AUDIENCES.map((au) => ({ key: au, label: AUDIENCE_META[au].label, color: AUDIENCE_META[au].color }));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono text-gray-400 flex items-center gap-1.5">
          <Grid3x3 size={11} className="text-cyan-400" />
          策略画布矩阵（颜色 = 平均 ROI，点格子看内容）
        </div>
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => { setView("type"); setSelected(null); }}
            className={`text-[8px] font-mono px-1.5 py-0.5 rounded transition ${view === "type" ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40" : "bg-white/[0.03] text-gray-500 border border-white/[0.06] hover:text-gray-300"}`}
          >
            内容类型×意图
          </button>
          <button
            onClick={() => { setView("audience"); setSelected(null); }}
            className={`flex items-center gap-0.5 text-[8px] font-mono px-1.5 py-0.5 rounded transition ${view === "audience" ? "bg-purple-500/20 text-purple-300 border border-purple-500/40" : "bg-white/[0.03] text-gray-500 border border-white/[0.06] hover:text-gray-300"}`}
          >
            <Users size={8} /> 目标人群×意图
          </button>
        </div>
      </div>

      {directions.length === 0 ? (
        <p className="text-[10px] text-gray-600 text-center py-4">暂无内容方向，先去「内容策略」tab 生成</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="text-left text-[9px] font-mono text-gray-600 px-2 py-1.5">
                  {view === "type" ? "内容类型＼意图" : "目标人群＼意图"}
                </th>
                {INTENTS.map((it) => (
                  <th key={it} className="text-[9px] font-mono px-2 py-1.5" style={{ color: INTENT_META[it].color }}>
                    {INTENT_META[it].label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <td className="text-[9px] font-mono px-2 py-1 whitespace-nowrap" style={{ color: row.color }}>
                    {row.label}
                  </td>
                  {INTENTS.map((it) => {
                    const cell = cellFor(it, row.key);
                    return (
                      <td key={it} className="p-1">
                        <button
                          onClick={() => setSelected(cell ? {
                            row: row.key, intent: it,
                            ...(view === "type" ? { type: row.key as ContentType } : { audience: row.key as Audience }),
                          } : null)}
                          disabled={!cell}
                          className={`w-full min-w-[80px] rounded border px-2 py-1.5 text-center transition ${cell ? "hover:brightness-110 cursor-pointer" : "opacity-20"}`}
                          style={{
                            borderColor: cell ? `${roiColor(cell.avgRoi)}44` : "rgba(255,255,255,0.04)",
                            background: cell ? `${roiColor(cell.avgRoi)}14` : "transparent",
                          }}
                        >
                          {cell ? (
                            <>
                              <div className="text-[11px] font-bold" style={{ color: roiColor(cell.avgRoi) }}>
                                {cell.count} 篇
                              </div>
                              <div className="text-[8px] font-mono text-gray-500">ROI {cell.avgRoi}</div>
                            </>
                          ) : (
                            <div className="text-[9px] text-gray-700">—</div>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && matrix[`${selected.intent}|${selected.row}`] && (
        <div className="rounded border border-cyan-500/20 bg-cyan-500/[0.03] p-2">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] font-mono" style={{ color: INTENT_META[selected.intent].color }}>
              {INTENT_META[selected.intent].label}
            </span>
            <span className="text-gray-600">×</span>
            <span className="text-[10px] font-mono" style={{ color: selected.type ? TYPE_META[selected.type].color : AUDIENCE_META[selected.audience as Audience].color }}>
              {selected.type ? TYPE_META[selected.type].label : AUDIENCE_META[selected.audience as Audience].label}
            </span>
            <button onClick={() => setSelected(null)} className="ml-auto text-gray-500 hover:text-white">
              <X size={10} />
            </button>
          </div>
          <div className="space-y-1">
            {matrix[`${selected.intent}|${selected.row}`].items.map((d, i) => (
              <div key={i} className="text-[10px] text-gray-300">
                <span className="text-cyan-400">#{d.roiScore || 0}</span> {d.title}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 text-[8px] font-mono text-gray-600">
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-[#10B981]" />ROI≥80</span>
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-[#F59E0B]" />ROI 60-79</span>
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-[#EF4444]" />ROI&lt;60</span>
        <span className="ml-auto">{directions.length} 个内容方向已落入矩阵</span>
      </div>
    </div>
  );
}
